import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Building2,
  Check,
  ChevronDown,
  ChevronsUpDown,
  LayoutDashboard,
  LayoutGrid,
  LogOut,
  Monitor,
  Moon,
  ScrollText,
  Shield,
  ShieldCheck,
  Sun,
  UserCog,
  Users,
  type LucideIcon,
  Cake,
  CalendarRange,
} from "lucide-react";
import { Suspense, useEffect, useRef, useState, type ReactNode } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { selectCompany } from "../lib/api";
import { authClient } from "../lib/auth-client";
import { initials } from "../lib/format";
import { useCan, useMe } from "../lib/session";
import { useTheme, type ThemePreference } from "../lib/theme";
import { GalewayLogo, GalewayMark } from "./Logo";
import { cx, Spinner } from "./ui";

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  permission?: string;
  /** Shown only to people granted this application. */
  app?: string;
  end?: boolean;
  /** Hidden from the compact mobile tab bar. */
  desktopOnly?: boolean;
}

const PRIMARY: NavItem[] = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/apps", label: "Apps", icon: LayoutGrid },
  { to: "/workforce", label: "Workforce", icon: CalendarRange, app: "workforce" },
  { to: "/directory", label: "Directory", icon: Users, permission: "employees.read", end: true },
  { to: "/directory/birthdays", label: "Birthdays", icon: Cake, permission: "employees.personal.read", desktopOnly: true },
];
const PLATFORM: NavItem[] = [
  { to: "/portfolio", label: "Portfolio", icon: Building2, end: true },
  { to: "/portfolio/applications", label: "App registry", icon: Shield },
];

/** Switch the company being viewed; a full reload guarantees no data from the previous company lingers. */
function switchCompany(id: string) {
  selectCompany(id);
  window.location.assign("/");
}

const ADMIN: NavItem[] = [
  { to: "/admin/members", label: "Users & access", icon: UserCog, permission: "users.read" },
  { to: "/admin/applications", label: "Applications", icon: Shield, permission: "apps.read" },
  { to: "/admin/audit", label: "Audit log", icon: ScrollText, permission: "audit.read" },
];

function CompanyBadge() {
  const me = useMe();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const accent = me.company.branding.accentColor;
  const canSwitch = me.companies.length > 1;
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  const badge = (
    <>
      {me.company.branding.logoUrl ? (
        <img src={me.company.branding.logoUrl} alt="" className="size-7 rounded-md object-cover" />
      ) : (
        <span
          className="flex size-7 shrink-0 items-center justify-center rounded-md text-[11px] font-bold text-white"
          style={{ background: accent ?? "#2563eb" }}
        >
          {initials(me.company.branding.shortName ?? me.company.name)}
        </span>
      )}
      <div className="min-w-0 flex-1 text-left">
        <p className="truncate text-[13px] font-semibold text-white">{me.company.name}</p>
        <p className="truncate text-[11.5px] text-white/55">{me.role.name}</p>
      </div>
    </>
  );
  if (!canSwitch) {
    return <div className="flex min-w-0 items-center gap-2.5 rounded-lg border border-white/10 bg-white/5 px-3 py-2.5">{badge}</div>;
  }
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Current company: ${me.company.name}. Switch company`}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full min-w-0 items-center gap-2.5 rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 transition-colors hover:bg-white/10"
      >
        {badge}
        <ChevronsUpDown className="size-4 shrink-0 text-white/50" aria-hidden />
      </button>
      {open && (
        <ul
          role="listbox"
          aria-label="Companies"
          className="absolute inset-x-0 top-full z-40 mt-1.5 overflow-hidden rounded-xl border border-line bg-surface py-1 shadow-pop"
        >
          {me.companies.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                role="option"
                aria-selected={c.id === me.company.id}
                onClick={() => (c.id === me.company.id ? setOpen(false) : switchCompany(c.id))}
                className="flex w-full items-center gap-2.5 px-3.5 py-2 text-left text-[13.5px] text-fg hover:bg-surface-2"
              >
                <span className="min-w-0 flex-1 truncate">{c.name}</span>
                {c.id === me.company.id && <Check className="size-4 text-accent" aria-hidden />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SidebarLink({ item }: { item: NavItem }) {
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) =>
        cx(
          "group flex h-10 items-center gap-3 rounded-lg px-3 text-[13.5px] font-medium transition-colors",
          isActive ? "bg-white/12 text-white shadow-[inset_2px_0_0_#5b8cff]" : "text-white/65 hover:bg-white/6 hover:text-white",
        )
      }
    >
      <item.icon className="size-[18px]" aria-hidden />
      {item.label}
    </NavLink>
  );
}

function ThemeMenuItems({ onDone }: { onDone: () => void }) {
  const { preference, setPreference } = useTheme();
  const opts: Array<{ v: ThemePreference; label: string; icon: LucideIcon }> = [
    { v: "system", label: "System", icon: Monitor },
    { v: "light", label: "Light", icon: Sun },
    { v: "dark", label: "Dark", icon: Moon },
  ];
  return (
    <div className="px-2 py-1.5">
      <p className="px-2 pb-1 text-[11px] font-medium uppercase tracking-wider text-subtle">Theme</p>
      <div className="grid grid-cols-3 gap-1">
        {opts.map((o) => (
          <button
            key={o.v}
            type="button"
            aria-pressed={preference === o.v}
            onClick={() => {
              setPreference(o.v);
              onDone();
            }}
            className={cx(
              "flex flex-col items-center gap-1 rounded-md py-2 text-[11.5px]",
              preference === o.v ? "bg-accent-soft text-accent" : "text-muted hover:bg-surface-3",
            )}
          >
            <o.icon className="size-4" />
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function UserMenu() {
  const me = useMe();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const signOut = useMutation({
    mutationFn: () => authClient.signOut(),
    onSettled: () => {
      qc.clear();
      navigate("/login", { replace: true });
    },
  });
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex h-10 items-center gap-2 rounded-lg pl-1 pr-2 hover:bg-surface-3"
      >
        <span className="flex size-8 items-center justify-center rounded-full bg-navy text-[12px] font-semibold text-white dark:bg-accent-soft dark:text-accent">
          {initials(me.user.name)}
        </span>
        <span className="hidden max-w-36 truncate text-[13px] font-medium sm:block">{me.user.name}</span>
        <ChevronDown className="size-4 text-subtle" aria-hidden />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 top-12 z-40 w-64 overflow-hidden rounded-xl border border-line bg-surface shadow-pop">
          <div className="border-b border-line px-4 py-3">
            <p className="truncate text-sm font-semibold">{me.user.name}</p>
            <p className="truncate text-[12.5px] text-muted">{me.user.email}</p>
          </div>
          <div className="py-1">
            <button
              role="menuitem"
              type="button"
              onClick={() => {
                setOpen(false);
                navigate("/account");
              }}
              className="flex w-full items-center gap-2.5 px-4 py-2 text-left text-[13.5px] hover:bg-surface-2"
            >
              <ShieldCheck className="size-4 text-muted" /> Account & security
            </button>
          </div>
          {me.companies.length > 1 && (
            <div className="border-t border-line py-1">
              <p className="px-4 pb-1 pt-1.5 text-[11px] font-medium uppercase tracking-wider text-subtle">Company</p>
              {me.companies.map((c) => (
                <button
                  key={c.id}
                  role="menuitemradio"
                  aria-checked={c.id === me.company.id}
                  type="button"
                  onClick={() => (c.id === me.company.id ? setOpen(false) : switchCompany(c.id))}
                  className="flex w-full items-center gap-2.5 px-4 py-2 text-left text-[13.5px] hover:bg-surface-2"
                >
                  <span className="min-w-0 flex-1 truncate">{c.name}</span>
                  {c.id === me.company.id && <Check className="size-4 text-accent" aria-hidden />}
                </button>
              ))}
            </div>
          )}
          <div className="border-t border-line">
            <ThemeMenuItems onDone={() => setOpen(false)} />
          </div>
          <div className="border-t border-line py-1">
            <button
              role="menuitem"
              type="button"
              onClick={() => signOut.mutate()}
              className="flex w-full items-center gap-2.5 px-4 py-2 text-left text-[13.5px] text-danger hover:bg-surface-2"
            >
              <LogOut className="size-4" /> Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const TITLES: Record<string, string> = {
  "/": "Dashboard",
  "/apps": "Apps",
  "/directory": "Directory",
  "/directory/birthdays": "Birthdays",
  "/workforce": "Workforce",
  "/workforce/settings": "Workforce settings",
  "/account": "Account & security",
  "/admin/members": "Users & access",
  "/admin/applications": "Applications",
  "/admin/audit": "Audit log",
  "/portfolio": "Portfolio",
  "/portfolio/applications": "App registry",
};

export function AppShell() {
  const me = useMe();
  const can = useCan();
  const location = useLocation();
  const primary = PRIMARY.filter((i) => (!i.permission || can(i.permission)) && (!i.app || me.appIds.includes(i.app)));
  const admin = ADMIN.filter((i) => !i.permission || can(i.permission));
  const platform = me.isPlatformAdmin ? PLATFORM : [];
  const section = TITLES[location.pathname] ?? "";

  useEffect(() => {
    document.title = section ? `${section} · Galeway | Operations Hub` : "Galeway | Operations Hub";
  }, [section]);

  const mobileItems: NavItem[] = [
    ...primary.filter((i) => !i.desktopOnly),
    ...(admin.length ? [{ to: "/admin/members", label: "Admin", icon: UserCog, permission: "users.read" }] : []),
    ...platform.slice(0, 1),
  ].filter((i) => !i.permission || can(i.permission));

  return (
    <div className="min-h-dvh lg:pl-64">
      <a href="#main" className="sr-only z-50 rounded-md bg-accent px-3 py-2 text-white focus:not-sr-only focus:fixed focus:left-3 focus:top-3">
        Skip to content
      </a>
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col bg-navy px-3 pb-4 pt-5 lg:flex">
        <div className="px-2">
          <GalewayLogo inverted />
          <p className="mt-1 pl-[42px] text-[11px] tracking-wide text-white/45">One Gateway. Every Business.</p>
        </div>
        <div className="mt-6">
          <CompanyBadge />
        </div>
        <nav aria-label="Main" className="mt-6 flex-1 space-y-1 overflow-y-auto">
          {primary.map((item) => (
            <SidebarLink key={item.to} item={item} />
          ))}
          {admin.length > 0 && (
            <>
              <p className="px-3 pb-1.5 pt-6 text-[11px] font-semibold uppercase tracking-wider text-white/40">Administration</p>
              {admin.map((item) => (
                <SidebarLink key={item.to} item={item} />
              ))}
            </>
          )}
          {platform.length > 0 && (
            <>
              <p className="px-3 pb-1.5 pt-6 text-[11px] font-semibold uppercase tracking-wider text-white/40">Gale Ventures</p>
              {platform.map((item) => (
                <SidebarLink key={item.to} item={item} />
              ))}
            </>
          )}
        </nav>
        <p className="px-3 text-[11px] text-white/35">A Gale Ventures platform</p>
      </aside>

      {/* Top bar */}
      <header className="sticky top-0 z-20 border-b border-line bg-surface/85 backdrop-blur supports-[backdrop-filter]:bg-surface/70">
        <div className="mx-auto flex h-16 max-w-[1400px] items-center gap-3 px-4 sm:px-6">
          <div className="flex items-center gap-2.5 lg:hidden">
            <GalewayMark className="size-8" />
            <div className="leading-tight">
              <p className="text-[15px] font-semibold">Galeway</p>
              <p className="flex items-center gap-1 text-[11.5px] text-muted">
                <Building2 className="size-3" aria-hidden />
                {me.company.branding.shortName ?? me.company.name}
              </p>
            </div>
          </div>
          <p className="hidden text-[13px] text-muted lg:block">
            <span className="text-subtle">{me.company.name}</span>
            {section && <span className="mx-2 text-line-strong">/</span>}
            <span className="font-medium text-fg">{section}</span>
          </p>
          <div className="ml-auto flex items-center gap-1">
            <UserMenu />
          </div>
        </div>
      </header>

      <main id="main" className="mx-auto max-w-[1400px] px-4 pb-28 pt-6 sm:px-6 lg:pb-12 lg:pt-8">
        <Suspense fallback={<Spinner />}>
          <Outlet />
        </Suspense>
      </main>

      {/* Mobile bottom navigation: large touch targets */}
      <nav
        aria-label="Main"
        className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-surface/95 pb-[env(safe-area-inset-bottom)] backdrop-blur lg:hidden"
      >
        <div className="mx-auto flex max-w-lg justify-around">
          {mobileItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cx(
                  "flex min-h-16 flex-1 flex-col items-center justify-center gap-1 text-[11px] font-medium",
                  isActive || (item.label === "Admin" && location.pathname.startsWith("/admin")) ? "text-accent" : "text-muted",
                )
              }
            >
              <item.icon className="size-5" aria-hidden />
              {item.label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}

export function AdminTabs() {
  const can = useCan();
  const items = ADMIN.filter((i) => !i.permission || can(i.permission));
  return (
    <div className="-mx-4 mb-5 overflow-x-auto px-4 lg:hidden">
      <div className="inline-flex gap-1 rounded-lg border border-line bg-surface p-1">
        {items.map((i) => (
          <NavLink
            key={i.to}
            to={i.to}
            className={({ isActive }) =>
              cx("whitespace-nowrap rounded-md px-3 py-1.5 text-[13px] font-medium", isActive ? "bg-accent-soft text-accent" : "text-muted")
            }
          >
            {i.label}
          </NavLink>
        ))}
      </div>
    </div>
  );
}

export function AuthLayout({ children, title, subtitle }: { children: ReactNode; title: string; subtitle?: ReactNode }) {
  useEffect(() => {
    document.title = `${title} · Galeway`;
  }, [title]);
  return (
    <div className="grid min-h-dvh lg:grid-cols-[1.05fr_1fr]">
      <div className="relative hidden overflow-hidden bg-navy lg:block">
        <div className="absolute inset-0 bg-[radial-gradient(1200px_600px_at_-10%_-10%,rgba(91,140,255,0.35),transparent_60%),radial-gradient(800px_500px_at_110%_110%,rgba(29,78,216,0.35),transparent_60%)]" />
        <img
          src="/brand/galeway-mark-white-512.png"
          alt=""
          aria-hidden
          draggable={false}
          className="pointer-events-none absolute -bottom-16 -right-20 w-[78%] max-w-[640px] select-none opacity-[0.07]"
        />
        <div className="relative flex h-full flex-col justify-between p-12 text-white">
          <GalewayLogo inverted />
          <div className="max-w-md">
            <p className="text-3xl font-semibold leading-tight tracking-tight">
              One Gateway.
              <br />
              Every Business.
            </p>
            <p className="mt-4 text-[15px] leading-relaxed text-white/65">
              Secure, single sign-on access to your company's applications, approvals and daily operations — in one place.
            </p>
          </div>
          <p className="text-xs text-white/40">Galeway is a Gale Ventures technology platform.</p>
        </div>
      </div>
      <div className="flex flex-col bg-surface lg:bg-bg">
        <div className="flex items-center justify-center px-6 pt-10 lg:hidden">
          <GalewayLogo />
        </div>
        <div className="flex flex-1 items-center justify-center px-5 py-10 sm:px-10">
          <div className="w-full max-w-[400px]">
            <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
            {subtitle && <p className="mt-1.5 text-sm text-muted">{subtitle}</p>}
            <div className="mt-8">{children}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
